import { useContext } from 'react';
import { useSelector } from 'react-redux';

import useAddressApi from '../../../commons/hooks/use-address-api';
import useCartTotals from '../../../store/cartTotals/useCartTotals';
import usePortalSettings from '../../../hooks/usePortalSettings';
import useApiStatus from '../../../store/apiStatus/useApiStatus';
import DataContext from '../../../store/dataContext';
import { status } from '../../../helpers/constants';
import UiContext from '../../../store/uiContext';
import { refreshCertificatesAndCoupons } from '../../../components/Coupon/CouponServices';

import { selectMethods, selectShipping } from '../../shipping/slice';
import { applySignature, removeSignature } from '../api/customer-signature';

const useCustomerSignature = () => {
  const {
    updateShippingMethodId,
    contextShippingAddress,
    setIsSignatureApplied,
    updateShippingMethods,
    updateCartDetails,
    shippingMethodId,
    cartId,
    device,
    email,
    updateCoupon,
    setPaymentMethodData,
    giftCertificates,
    updateGiftInfo,
    additionalCharges,
    updateAdditionalCharges,
  } = useContext(DataContext);
  const { setIsLoadingSignature } = useContext(UiContext);
  const { setData: setCartTotalsData } = useCartTotals();
  const { portalSettings } = usePortalSettings();
  const { set: setApiStatus } = useApiStatus();
  const { getWithShippingMethodPayload } = useAddressApi();
  const shipping = useSelector(selectShipping);
  const methods = useSelector(selectMethods);

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoadingSignature(true);
    const charges = [
      ...additionalCharges.filter(item => item.name !== 'Signature Required'),
      {
        name: 'Signature Required',
        qty: 1,
        value: portalSettings.customerSignature.quote,
      },
    ];
    const payload = getWithShippingMethodPayload(
      shipping.address,
      shipping.fullName.value,
      cartId,
      shipping.methods.selectedKey,
      shipping.additionalData?.companyName,
      charges,
    );
    const applyProtectionResponce = await applySignature({
      payload,
      cartId,
      email,
      deviceId: device?.id,
      contextShippingAddress,
      quote: portalSettings.customerSignature.quote,
      shippingMethodId,
      methods,
    });
    if (applyProtectionResponce.success) {
      updateAdditionalCharges(charges);
      const { coupons } = applyProtectionResponce.cart;
      setIsSignatureApplied(true);
      setIsLoadingSignature(false);
      updateCartDetails(applyProtectionResponce.cart);
      if (applyProtectionResponce.cartTotals && Object.keys(applyProtectionResponce.cartTotals).length > 0) {
        setCartTotalsData(applyProtectionResponce.cartTotals);
      }
      if (contextShippingAddress && contextShippingAddress.line1) {
        updateShippingMethods(applyProtectionResponce.shipMethods);
        updateShippingMethodId(applyProtectionResponce.currentMethod);
        setApiStatus({ isShippingMethodReady: status.READY });
        setPaymentMethodData(applyProtectionResponce.paymentMethodData);
      }
      const { refreshedCertificates, refreshedCoupons } = await refreshCertificatesAndCoupons({
        giftCertificates,
        coupons,
      });
      if (refreshedCertificates.length > 0) {
        updateGiftInfo(refreshedCertificates);
      }
      if (refreshedCoupons) {
        updateCoupon(refreshedCoupons);
      }
    }
  };

  const handleRemove = async () => {
    const charges = additionalCharges.filter(item => item.name !== 'Signature Required');
    setIsLoadingSignature(true);
    const payload = getWithShippingMethodPayload(
      shipping.address,
      shipping.fullName.value,
      cartId,
      shipping.methods.selectedKey,
      shipping.additionalData?.companyName,
      charges,
    );
    const removeProtectionResult = await removeSignature({
      payload,
      cartId,
      quote: portalSettings.customerSignature.quote,
      deviceId: device?.id,
      email,
      contextShippingAddress,
      shippingMethodId,
      methods,
    });
    if (removeProtectionResult.success) {
      updateAdditionalCharges(charges);
      const { coupons } = removeProtectionResult.cart;
      updateCartDetails(removeProtectionResult.cart);
      if (removeProtectionResult.cartTotals && Object.keys(removeProtectionResult.cartTotals).length > 0) {
        setCartTotalsData(removeProtectionResult.cartTotals);
      }
      if (contextShippingAddress && Object.keys(contextShippingAddress).length > 0) {
        updateShippingMethods(removeProtectionResult.shipMethods);
        updateShippingMethodId(removeProtectionResult.currentMethod);
        setPaymentMethodData(removeProtectionResult.paymentMethodData);
      }
      setIsLoadingSignature(false);
      setIsSignatureApplied(false);
      const { refreshedCertificates, refreshedCoupons } = await refreshCertificatesAndCoupons({
        giftCertificates,
        coupons,
      });
      if (refreshedCertificates.length > 0) {
        updateGiftInfo(refreshedCertificates);
      }
      if (refreshedCoupons) {
        updateCoupon(refreshedCoupons);
      }
    }
  };

  return { handleSubmit, handleRemove };
};

export default useCustomerSignature;
